"use client";

import { ChangeEvent, useState } from "react";
import { BriefPdf, generateUUID, MAX_BRIEF_PDFS, toast } from "@drpcrd/utils";
import type { BriefPdfUploadTarget } from "../../brief-pdfs/storage";

/**
 * Uploads one PDF and resolves with the key it landed under. The host app's
 * uploads route issues the key and a signed URL; the file goes there in one
 * PUT.
 */
const uploadPdf = async (file: File): Promise<string> => {
  const targetRes = await fetch("/api/brief-pdfs/uploads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fileType: file.type }),
  });
  if (!targetRes.ok) {
    throw new Error("Failed to start the upload");
  }
  const target: BriefPdfUploadTarget = await targetRes.json();

  const res = await fetch(target.url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!res.ok) {
    throw new Error("Failed to upload the file");
  }
  return target.key;
};

type PendingUpload = { id: string; name: string };

type Options = {
  /** How many PDFs the brief already has, so the cap counts them too. */
  attachedCount: number;
  onUploaded: (pdf: BriefPdf) => void;
};

/**
 * Uploads the PDFs picked in a file input, several at once, and hands each
 * one back as it lands. Enforces the brief's PDF cap across what is already
 * attached and what is still in flight, and reports a failed file with a
 * toast without stopping the others.
 */
export const useBriefPdfUploads = ({ attachedCount, onUploaded }: Options) => {
  const [pending, setPending] = useState<PendingUpload[]>([]);

  const onFilesSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    // Clear the input so picking the same file again still fires a change.
    event.target.value = "";

    const files = picked.filter((file) => file.type === "application/pdf");
    if (files.length < picked.length) {
      toast.error("Only PDF files can be attached");
    }
    if (files.length === 0) {
      return;
    }

    const room = Math.max(MAX_BRIEF_PDFS - attachedCount - pending.length, 0);
    if (files.length > room) {
      toast.error(`You can attach up to ${MAX_BRIEF_PDFS} PDFs`, {
        description:
          room > 0
            ? `Only the first ${room} of your selection will be added`
            : "Remove one to add another",
      });
    }

    const uploads = files
      .slice(0, room)
      .map((file) => ({ id: generateUUID(), name: file.name, file }));
    setPending((previous) => [
      ...previous,
      ...uploads.map(({ id, name }) => ({ id, name })),
    ]);

    await Promise.all(
      uploads.map(async ({ id, name, file }) => {
        try {
          const key = await uploadPdf(file);
          onUploaded({ id: key, name });
        } catch {
          toast.error(`Couldn’t upload ${name}`, {
            description: "Please try again",
          });
        } finally {
          setPending((previous) =>
            previous.filter((upload) => upload.id !== id),
          );
        }
      }),
    );
  };

  return { pending, onFilesSelected };
};
